import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';
import { Brain, Play, Pause, RefreshCw, AlertCircle, CheckCircle, TrendingUp } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { SystemIntegrationStatus } from './SystemIntegrationStatus';

interface RoleStats {
  role: string;
  current: number;
  target: number;
  percentage: number;
}

interface OperationStatus {
  id: string;
  status: string;
  generated_count: number;
  total_target: number;
  started_at: string;
  metadata: any;
}

export const UnifiedQuestionDashboard: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [operation, setOperation] = useState<OperationStatus | null>(null);
  const [roleStats, setRoleStats] = useState<RoleStats[]>([]);
  const [totalCurrent, setTotalCurrent] = useState(0);
  const [totalTarget] = useState(100000);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    try {
      // Get current operation
      const { data: ops } = await supabase
        .from('question_generation_operations')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(1)
        .single();

      setOperation(ops);

      // Get total count
      const { count } = await supabase
        .from('interview_questions')
        .select('*', { count: 'exact', head: true });

      setTotalCurrent(count || 0);

      // Get role distribution
      const { data: questions } = await supabase
        .from('interview_questions')
        .select('role_type');

      const roleCounts: Record<string, number> = {};
      questions?.forEach((q: any) => {
        roleCounts[q.role_type] = (roleCounts[q.role_type] || 0) + 1;
      });

      // Calculate stats for top roles
      const defaultDistribution = getDefaultRoleDistribution();
      const stats: RoleStats[] = Object.entries(defaultDistribution)
        .map(([role, target]) => ({
          role,
          current: roleCounts[role] || 0,
          target,
          percentage: ((roleCounts[role] || 0) / target) * 100
        }))
        .sort((a, b) => b.current - a.current)
        .slice(0, 20);

      setRoleStats(stats);
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const startGeneration = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.functions.invoke('generate-interview-questions', {
        body: { action: 'start' }
      });

      if (error) throw error;

      toast({
        title: 'Generation Started',
        description: 'AI question generation is now running with role distribution.',
      });

      fetchStatus();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const stopGeneration = async () => {
    if (!operation) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('question_generation_operations')
        .update({ status: 'stopped' })
        .eq('id', operation.id);

      if (error) throw error;

      toast({
        title: 'Generation Stopped',
        description: 'Question generation has been paused.',
      });

      fetchStatus();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const overallProgress = (totalCurrent / totalTarget) * 100;

  return (
    <div className="space-y-6">
      {/* System Integration Status */}
      <SystemIntegrationStatus />
      
      {/* Overall Progress */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5" />
                100k Question Generation - Role Distribution System
              </CardTitle>
              <CardDescription>
                Generating {totalTarget.toLocaleString()} questions across 64 roles with balanced distribution
              </CardDescription>
            </div>
            {operation?.status === 'in_progress' ? (
              <Button onClick={stopGeneration} disabled={loading} variant="destructive">
                <Pause className="h-4 w-4 mr-2" />
                Stop
              </Button>
            ) : (
              <Button onClick={startGeneration} disabled={loading}>
                <Play className="h-4 w-4 mr-2" />
                Start Generation
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium">Overall Progress</span>
              <span className="text-muted-foreground">
                {totalCurrent.toLocaleString()} / {totalTarget.toLocaleString()} ({overallProgress.toFixed(1)}%)
              </span>
            </div>
            <Progress value={overallProgress} className="h-3" />
          </div>

          {operation && (
            <div className="flex items-center gap-4 text-sm">
              <Badge variant={operation.status === 'in_progress' ? 'default' : 'secondary'}>
                {operation.status}
              </Badge>
              <span className="text-muted-foreground">
                Started: {new Date(operation.started_at).toLocaleString()}
              </span>
              {operation.metadata?.lastGeneratedRole && (
                <span className="text-muted-foreground">
                  Current Role: {operation.metadata.lastGeneratedRole}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Role Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Role Distribution (Top 20)
          </CardTitle>
          <CardDescription>
            Questions generated per role with target distribution
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {roleStats.map((stat) => (
              <div key={stat.role} className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="font-medium capitalize">
                    {stat.role.replace(/-/g, ' ')}
                  </span>
                  <span className="text-muted-foreground">
                    {stat.current} / {stat.target} ({stat.percentage.toFixed(1)}%)
                  </span>
                </div>
                <Progress value={stat.percentage} className="h-2" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* System Info */}
      <Card>
        <CardHeader>
          <CardTitle>System Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <div className="font-medium">Intelligent Role Selection</div>
                <div className="text-sm text-muted-foreground">
                  Automatically prioritizes roles with the largest deficit
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <div className="font-medium">Duplicate Prevention</div>
                <div className="text-sm text-muted-foreground">
                  Checks existing questions before inserting
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <div className="font-medium">Auto-Recovery</div>
                <div className="text-sm text-muted-foreground">
                  Automatically resumes failed operations
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <div className="font-medium">Role-Specific Context</div>
                <div className="text-sm text-muted-foreground">
                  Each question is tailored to the specific role's technologies and focus areas
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

function getDefaultRoleDistribution(): Record<string, number> {
  return {
    'frontend-developer': 2500,
    'backend-developer': 2500,
    'fullstack-developer': 2500,
    'mobile-developer': 1800,
    'ios-developer': 1200,
    'android-developer': 1200,
    'devops-engineer': 1800,
    'data-scientist': 1800,
    'data-engineer': 1800,
    'ml-engineer': 1800,
    'product-manager': 1500,
    'engineering-manager': 1500,
    'qa-engineer': 1500,
    'security-engineer': 1500,
    'cloud-engineer': 1500,
    'ai-engineer': 1500,
    'tech-lead': 1200,
    'cloud-architect': 1300,
    'data-analyst': 1200,
    'test-automation-engineer': 1200
  };
}
